import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useApiClient from '../api/useApiClient';
import useHistory from '../navigation/useHistory';
import { ScreenEnum } from '../navigation/Screens';

/**
 * Open the onboarding page for a given restaurant.
 *
 * The onboarding route carries no company id — it always runs against the
 * restaurant selected in the header (and `HasSelectedCompanyGuard` sends you
 * home when none is). So any entry point that targets a *specific* restaurant
 * (a dashboard card, the owner's "continue setup" banner) has to select it
 * first, exactly as picking it in the sidebar would, and only then navigate.
 */
export default function useOpenOnboarding() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Hooks:UseOpenOnboarding' });
  const { CompaniesManager } = useApiClient();
  const history = useHistory();

  const { company } = useSelector((state) => state.currentCompany);
  const [opening, setOpening] = useState(false);

  const open = useCallback(
    async (companyId) => {
      // Already the selected restaurant — nothing to switch, just go.
      if (!companyId || String(companyId) === String(company?.id)) {
        history.push(ScreenEnum.RestaurantOnboarding);

        return true;
      }

      setOpening(true);
      // `show` is what the sidebar picker uses; it selects the company into the
      // header (and the api client's tenant headers) on success.
      const { success } = await CompaniesManager.show(companyId);
      setOpening(false);

      if (!success) {
        toast.error(t('failed_to_open'));

        return false;
      }

      history.push(ScreenEnum.RestaurantOnboarding);

      return true;
    },
    [company?.id, CompaniesManager, history, t],
  );

  return { open, opening };
}
